import { Popover } from 'antd';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { operatorRequiresMultipleValues, operatorRequiresValue } from '../core/operators';
import type { FieldConfig, Filter, FilterConfig, FilterOperator, TextSearchFieldConfig } from '../core/types';
import { FilterBuilder } from './FilterBuilder';
import { OperatorBadge } from './OperatorBadge';

interface FilterChipProps<TWhere> {
    filter: Filter;
    config: FilterConfig<TWhere>;
    whereClause?: TWhere;
    isExpanded?: boolean;
    onToggleExpand: () => void;
    onUpdate: (updates: Partial<Filter>) => void;
    onRemove: () => void;
}

type OptionItem = { label: string; value: any };

/**
 * Formats a filter value for compact display in the chip
 */
function formatValueForDisplay(
    value: any,
    fieldConfig: FieldConfig<any, any> | TextSearchFieldConfig<any>,
    operator: FilterOperator,
    resolvedOptions?: OptionItem[],
): string {
    // Handle null/undefined
    if (value === null || value === undefined) {
        return '';
    }

    // Handle arrays
    if (Array.isArray(value)) {
        if (value.length === 0) return '';

        // For "between" operators, show both values with arrow
        if (operatorRequiresMultipleValues(operator) && value.length === 2) {
            const from = formatSingleValue(value[0], fieldConfig, resolvedOptions);
            const to = formatSingleValue(value[1], fieldConfig, resolvedOptions);
            return `${from} → ${to}`;
        }

        if (value.length === 1) {
            return formatSingleValue(value[0], fieldConfig, resolvedOptions);
        }
        // Multiple values (multiselect) - show first two + count
        const first = formatSingleValue(value[0], fieldConfig, resolvedOptions);
        const second = formatSingleValue(value[1], fieldConfig, resolvedOptions);
        if (value.length === 2) {
            return `${first}, ${second}`;
        }
        return `${first}, ${second} +${value.length - 2}`;
    }

    return formatSingleValue(value, fieldConfig, resolvedOptions);
}

/**
 * Compares two values, handling type coercion for bigint/number/string mismatches
 */
function valuesMatch(a: any, b: any): boolean {
    if (a === b) return true;
    // Handle bigint/number/string comparisons
    if (a == null || b == null) return false;
    return String(a) === String(b);
}

/**
 * Formats a single value, looking up labels from options if available
 */
function formatSingleValue(
    value: any,
    fieldConfig: FieldConfig<any, any> | TextSearchFieldConfig<any>,
    resolvedOptions?: OptionItem[],
): string {
    if (value === null || value === undefined) {
        return '';
    }

    // Try to find label from resolved options first (for async options)
    if (resolvedOptions) {
        const option = resolvedOptions.find((opt) => valuesMatch(opt.value, value));
        if (option) {
            return option.label;
        }
    }

    // Try to find label from static options
    if (Array.isArray(fieldConfig.options)) {
        const option = fieldConfig.options.find((opt) => valuesMatch(opt.value, value));
        if (option) {
            return option.label;
        }
    }

    // Use custom format function if provided
    if (fieldConfig.format) {
        try {
            return fieldConfig.format(value);
        } catch {
            // Fall through to default formatting
        }
    }

    // Boolean formatting
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    // Date formatting
    if (value instanceof Date) {
        return value.toLocaleDateString();
    }

    // BigInt formatting
    if (typeof value === 'bigint') {
        return String(value);
    }

    // Default string conversion
    return String(value);
}

/**
 * FilterChip Component
 *
 * A compact pill-style display for an active filter.
 * Shows: Field Label [operator icon] Value
 *
 * Clicking the chip opens a popover with the FilterBuilder for editing.
 * The remove button (×) closes the filter without opening the popover.
 */
export function FilterChip<TWhere>({
    filter,
    config,
    whereClause,
    isExpanded,
    onToggleExpand,
    onUpdate,
    onRemove,
}: FilterChipProps<TWhere>) {
    const fieldConfig = config.filters.find((f) => f.id === filter.filterId);

    // State for async options
    const [resolvedOptions, setResolvedOptions] = useState<OptionItem[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const optionsLoadedRef = useRef(false);
    const lastFieldIdRef = useRef(filter.filterId);

    // Load async options for display
    useEffect(() => {
        if (!fieldConfig) return;

        // Reset if field changed
        if (lastFieldIdRef.current !== filter.filterId) {
            optionsLoadedRef.current = false;
            lastFieldIdRef.current = filter.filterId;
            setResolvedOptions([]);
            setIsLoadingOptions(false);
        }

        // If options are already an array, use them
        if (Array.isArray(fieldConfig.options)) {
            setResolvedOptions(fieldConfig.options);
            setIsLoadingOptions(false);
            return;
        }

        // Load async options
        if (typeof fieldConfig.options === 'function' && !optionsLoadedRef.current) {
            optionsLoadedRef.current = true;
            setIsLoadingOptions(true);
            const optionsFunc = fieldConfig.options;
            const loadPromise = optionsFunc.length > 0 ? optionsFunc(whereClause) : optionsFunc();

            loadPromise
                .then((options) => {
                    setResolvedOptions(options);
                    setIsLoadingOptions(false);
                })
                .catch((error) => {
                    console.error(error);
                    setIsLoadingOptions(false);
                });
        }
    }, [fieldConfig, filter.filterId, whereClause]);

    if (!fieldConfig) return null;

    const showValue = operatorRequiresValue(filter.operator);
    const formattedValue = showValue
        ? formatValueForDisplay(filter.value, fieldConfig, filter.operator, resolvedOptions)
        : null;

    // Check if we're still loading async options and the value hasn't been resolved yet
    const hasAsyncOptions = typeof fieldConfig.options === 'function';
    const valueNotResolved =
        hasAsyncOptions &&
        filter.value != null &&
        formattedValue === String(filter.value) &&
        resolvedOptions.length === 0;
    const showSkeleton = isLoadingOptions || valueNotResolved;

    // Handle remove button click without triggering chip expansion
    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove();
    };

    const chipContent = (
        <div
            onClick={onToggleExpand}
            className={`group inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-sm transition-all ${
                isExpanded
                    ? 'border-accent-400 bg-accent-500/10'
                    : 'border-separator bg-card hover:border-muted-foreground'
            } `}
        >
            {/* Field label */}
            <span className="font-medium text-muted-foreground">{fieldConfig.label}</span>

            {/* Operator badge */}
            <OperatorBadge operator={filter.operator} fieldType={fieldConfig.type} withTooltip />

            {/* Value */}
            {showValue && formattedValue && (
                <span className="max-w-[200px] truncate font-semibold text-foreground">
                    {showSkeleton ? (
                        <span className="inline-flex items-center gap-0.5">
                            <span className="h-1 w-1 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.3s]" />
                            <span className="h-1 w-1 animate-bounce rounded-full bg-gray-400 [animation-delay:-0.15s]" />
                            <span className="h-1 w-1 animate-bounce rounded-full bg-gray-400" />
                        </span>
                    ) : (
                        formattedValue
                    )}
                </span>
            )}

            {/* Remove button */}
            <button
                onClick={handleRemove}
                className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={`Remove ${fieldConfig.label} filter`}
            >
                <X size={12} />
            </button>
        </div>
    );

    const popoverContent = (
        <FilterBuilder
            filter={filter}
            config={config}
            whereClause={whereClause}
            onUpdate={onUpdate}
            onConfirm={onToggleExpand}
            onRemove={() => {
                onRemove();
                onToggleExpand();
            }}
            autoFocus
        />
    );

    return (
        <Popover
            content={popoverContent}
            trigger="click"
            open={isExpanded}
            onOpenChange={(open) => {
                if (!open && isExpanded) {
                    onToggleExpand();
                }
            }}
            placement="bottomLeft"
            arrow={false}
            classNames={{
                root: 'filter-chip-popover',
            }}
            destroyOnHidden
        >
            {chipContent}
        </Popover>
    );
}
